// In-process transport for the netcode debug harness (/debug/net). Wires client
// "sockets" (SocketLike-shaped, exactly what the real client codes against) to an
// in-page server, with tunable per-direction latency / jitter / loss so we can
// reproduce real-network behaviour that localhost never shows. Messages are
// deep-copied on every hop (the real client mutates state in place; sharing an
// object would corrupt the server's authoritative truth) - this also faithfully
// mimics the JSON wire.

import type { SocketLike } from "../../../online/ds-socket";

export interface NetConditions {
  ping: number; // simulated round-trip ms (split evenly each direction)
  jitter: number; // +/- ms random variance per message
  loss: number; // fraction of messages dropped, 0..1
}

// One recorded wire message (see getLog). Times are ms since the bus was created.
export interface BusLogEntry {
  seq: number; // global order across both directions
  dir: "c2s" | "s2c"; // client-to-server or server-to-client
  from: string; // clientId or "server"
  to: string; // "server" or clientId
  type: string; // msg.type
  sentAt: number; // when it was enqueued
  deliverAt: number; // scheduled delivery (== sentAt if no latency; ignore if dropped)
  dropped?: boolean; // dropped by the loss model, never delivered
  gameTime?: number; // best-effort sim clock carried by the message
  msg: any; // full payload, snapshotted at send time (before the receiver mutates it)
}

interface ClientEntry {
  id: string;
  shim: SocketLike & {
    _deliver: (msg: any) => void;
    _connect: () => void;
    _timers: Set<any>;
  };
}

export interface HarnessBus {
  // Live, mutable network conditions (shared object; tweak fields to retune live).
  conditions: NetConditions;
  // Called by the server to receive a client's message: (clientId, msg).
  onServerMessage: (cb: (clientId: string, msg: any) => void) => void;
  // Server -> one client, or all clients.
  sendTo: (clientId: string, msg: any) => void;
  broadcast: (msg: any) => void;
  // Create a new client socket (fires its synthetic "connect" after a tick).
  addClient: (id: string) => SocketLike;
  clientIds: () => string[];
  // Recorded wire log (both directions, in send order) for offline analysis, and
  // a reset. Everything that crosses the bus is captured, including dropped msgs.
  getLog: () => BusLogEntry[];
  clearLog: () => void;
  dispose: () => void;
}

function clone(o: any) {
  return o == null ? o : JSON.parse(JSON.stringify(o));
}

// Best-effort sim clock for a message, so the log can be aligned to game-time
// (not just wall-clock): game_start / game_update carry it directly, worm_segments
// via their last segment's endTime.
function gameTimeOf(msg: any): number | undefined {
  if (msg == null) return undefined;
  if (msg.gameState && typeof msg.gameState.gameTime === "number") return msg.gameState.gameTime;
  if (msg.data && typeof msg.data.gameTime === "number") return msg.data.gameTime;
  if (Array.isArray(msg.segments) && msg.segments.length) {
    const last = msg.segments[msg.segments.length - 1];
    if (last && typeof last.endTime === "number") return last.endTime;
  }
  return undefined;
}

export function createHarnessBus(initial: Partial<NetConditions> = {}): HarnessBus {
  const conditions: NetConditions = { ping: 0, jitter: 0, loss: 0, ...initial };
  const clients = new Map<string, ClientEntry>();
  let serverOnMessage: (clientId: string, msg: any) => void = () => {};
  const allTimers = new Set<any>();
  // Monotonic release cursor per direction so jitter never reorders (TCP-like).
  const lastRelease = { in: 0, out: 0 };

  // Wire log: every message that crosses the bus, snapshotted at send time.
  const logT0 = Date.now();
  let log: BusLogEntry[] = [];
  let logSeq = 0;
  function record(dir: "c2s" | "s2c", meta: { from: string; to: string; msg: any }, sentAt: number, deliverAt: number, dropped: boolean) {
    log.push({
      seq: logSeq++, dir, from: meta.from, to: meta.to, type: meta.msg?.type ?? "?",
      sentAt: sentAt - logT0, deliverAt: deliverAt - logT0,
      ...(dropped ? { dropped: true } : {}), gameTime: gameTimeOf(meta.msg), msg: clone(meta.msg),
    });
  }

  // Schedule a delivery after the one-way delay (dropping on loss), keeping order.
  // meta (when given) taps the message into the wire log, including drops.
  function deliver(dir: "in" | "out", fn: () => void, timerBag: Set<any>, meta?: { from: string; to: string; msg: any }) {
    const now = Date.now();
    const logDir = dir === "in" ? "c2s" : "s2c";
    if (conditions.loss > 0 && Math.random() < conditions.loss) {
      if (meta) record(logDir, meta, now, now, true);
      return; // dropped
    }
    const oneWay = conditions.ping / 2;
    if (oneWay <= 0 && conditions.jitter <= 0) {
      if (meta) record(logDir, meta, now, now, false);
      fn(); return;
    }
    const base = now + Math.max(0, oneWay + (Math.random() * 2 - 1) * conditions.jitter);
    const releaseAt = Math.max(base, lastRelease[dir] + 1);
    lastRelease[dir] = releaseAt;
    if (meta) record(logDir, meta, now, releaseAt, false);
    const t = setTimeout(() => { timerBag.delete(t); allTimers.delete(t); fn(); }, releaseAt - now);
    timerBag.add(t);
    allTimers.add(t);
  }

  function addClient(id: string): SocketLike {
    const listeners = new Map<string, Set<(msg: any) => void>>();
    const pendingAcks = new Map<string, { cb: (e: any, r?: any) => void; timer: any }>();
    const timers = new Set<any>();
    let ackSeq = 0;

    function dispatch(event: string, msg: any) {
      const set = listeners.get(event);
      if (!set) return;
      for (const cb of [...set]) { try { cb(msg); } catch (e) { console.error("[harness-bus] listener", e); } }
    }

    const shim: any = {
      connected: false,
      id,
      on(event: string, cb: (msg: any) => void) { if (!listeners.has(event)) listeners.set(event, new Set()); listeners.get(event)!.add(cb); return shim; },
      off(event: string, cb?: (msg: any) => void) { if (cb) listeners.get(event)?.delete(cb); else listeners.delete(event); return shim; },
      emit(event: string, payload?: any) {
        const msg = { type: event, ...(payload && typeof payload === "object" ? payload : {}) };
        deliver("in", () => serverOnMessage(id, clone(msg)), timers, { from: id, to: "server", msg });
        return shim;
      },
      timeout(ms: number) {
        return {
          emit(event: string, payload: any, ack: (err: any, res?: any) => void) {
            const reqId = "a" + ++ackSeq;
            const timer = setTimeout(() => { pendingAcks.delete(reqId); ack(new Error("timeout")); }, ms);
            pendingAcks.set(reqId, { cb: ack, timer });
            const msg = { type: event, reqId, ...(payload && typeof payload === "object" ? payload : {}) };
            deliver("in", () => serverOnMessage(id, clone(msg)), timers, { from: id, to: "server", msg });
          },
        };
      },
      removeAllListeners() { listeners.clear(); return shim; },
      disconnect() { shim.connected = false; timers.forEach((t: any) => clearTimeout(t)); timers.clear(); clients.delete(id); },
      // Bus -> this client.
      _deliver(msg: any) {
        if (msg.reqId && pendingAcks.has(msg.reqId)) {
          const p = pendingAcks.get(msg.reqId)!;
          clearTimeout(p.timer); pendingAcks.delete(msg.reqId); p.cb(null, msg);
          return;
        }
        dispatch(msg.type, msg);
      },
      _connect() { shim.connected = true; dispatch("connect", undefined); },
      _timers: timers,
    };

    clients.set(id, { id, shim });
    // Fire "connect" on a later tick, like a real socket opening.
    const t = setTimeout(() => { allTimers.delete(t); shim._connect(); }, 0);
    allTimers.add(t);
    return shim;
  }

  function sendTo(clientId: string, msg: any) {
    const entry = clients.get(clientId);
    if (!entry) return;
    deliver("out", () => entry.shim._deliver(clone(msg)), entry.shim._timers, { from: "server", to: clientId, msg });
  }
  function broadcast(msg: any) {
    for (const id of clients.keys()) sendTo(id, msg);
  }

  return {
    conditions,
    onServerMessage: (cb) => { serverOnMessage = cb; },
    sendTo,
    broadcast,
    addClient,
    clientIds: () => [...clients.keys()],
    getLog: () => log,
    clearLog: () => { log = []; logSeq = 0; },
    dispose: () => { allTimers.forEach((t) => clearTimeout(t)); allTimers.clear(); clients.clear(); },
  };
}
